import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useReportingLastImportOverview } from "../api/useReportingLastImport";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { ROUTES } from "@/constants/routes";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import { useIntegrations } from "@/features/integrations/hooks/useIntegrations";
import { useModuleConfigurationSchema } from "@/features/integrations/hooks/useModuleConfiguration";
import { renderComponent } from "@/features/integrations/shared/renderer";
import type { RenderContext } from "@/features/integrations/shared/renderer/types";
import { decodeSlug, encodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import {
  useModuleStatusFilterSearchParams,
  type ModuleStatusFilter,
} from "@/lib/hooks/useModuleStatusFilterSearchParams";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";

function TenantReportingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { integrationName } = useParams<{ integrationName: string }>();
  const { tenantId } = useAuth();

  const decodedName = integrationName ? decodeSlug(integrationName) : null;
  const backendName = decodedName ?? "";

  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { moduleStatus, setModuleStatus } = useModuleStatusFilterSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler();

  const integrationsQuery = useIntegrations({
    tenantId,
    clientId: null,
  });
  const integration = integrationsQuery.data?.find(
    (i) => i.name.toLowerCase() === backendName?.toLowerCase(),
  );
  const integrationBreadcrumbLabel = integration?.displayName
    ? t(integration.displayName)
    : (integration?.name ?? decodedName ?? "Integration");

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.integrations"), href: `/${ROUTES.integrations}` },
      {
        label: integrationBreadcrumbLabel,
        href: integrationName
          ? `/${ROUTES.integrations}/${integrationName}`
          : undefined,
      },
      { label: t("integrations.modules.names.reporting", "Reporting") },
    ],
  });

  const moduleQuery = useIntegrationModule({
    integrationName: backendName,
    moduleName: "reporting",
    tenantId: tenantId ?? undefined,
    clientId: undefined,
  });

  const schemaQuery = useModuleConfigurationSchema(
    backendName,
    "reporting",
    { level: "tenant", tenantId: tenantId ?? null },
    !!backendName,
  );

  const lastImport = useReportingLastImportOverview({
    integrationName: backendName,
    tenantId: tenantId ?? undefined,
    page,
    pageSize,
    search: debouncedSearchValue,
    moduleStatus:
      moduleStatus === "all" ? undefined : moduleStatus === "active",
    sortColumn: orderBy ?? undefined,
    sortDirection:
      descending === undefined ? undefined : descending ? "desc" : "asc",
  });

  useEffect(() => {
    if (moduleQuery.isPending) return;
    const shouldRedirect = moduleQuery.isError || !moduleQuery.data?.isActive;
    if (shouldRedirect && integrationName) {
      navigate(`/${ROUTES.integrations}/${integrationName}`, { replace: true });
    }
  }, [
    moduleQuery.isPending,
    moduleQuery.isError,
    moduleQuery.data,
    integrationName,
    navigate,
  ]);

  const executeAction = useCallback<
    RenderContext["executeAction"]
  >(async () => {}, []);

  const onFilterChange = useCallback(
    (key: string, value: unknown) => {
      if (key === "moduleActive") {
        setModuleStatus(((value as string) || "all") as ModuleStatusFilter);
      }
    },
    [setModuleStatus],
  );

  const onRowClick = useCallback(
    (row: unknown) => {
      const id = (row as Record<string, unknown>).clientId as
        | string
        | undefined;
      if (decodedName && id) {
        navigate(
          `/${ROUTES.clients}/${id}/${ROUTES.integrations}/${encodeSlug(decodedName)}/reporting`,
        );
      }
    },
    [decodedName, navigate],
  );

  const ui = schemaQuery.data?.ui ?? [];
  const tableData = useMemo(
    () => lastImport.data?.data ?? [],
    [lastImport.data],
  );
  const isLoading = lastImport.isLoading || lastImport.isFetching;
  const totalRecords = lastImport.data?.totalRecords ?? 0;

  const context = useMemo<RenderContext>(
    () => ({
      integrationName: backendName,
      moduleName: "reporting",
      tenantId: tenantId ?? undefined,
      clientId: undefined,
      executeAction,
      tableData,
      isLoading,
      pagination: {
        page,
        pageSize,
        totalRecords,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      },
      orderBy: orderBy ?? null,
      descending: descending ?? false,
      onSort: handleSort as RenderContext["onSort"],
      emptyMessage: searchValue
        ? t("common.noResultsFound", "No results found")
        : t("reporting.noLastImportData", "No reporting data available"),
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      filterValues: { moduleActive: moduleStatus },
      onFilterChange,
      onRowClick,
    }),
    [
      backendName,
      tenantId,
      executeAction,
      tableData,
      isLoading,
      page,
      pageSize,
      totalRecords,
      setPage,
      setPageSize,
      orderBy,
      descending,
      handleSort,
      t,
      searchValue,
      setSearchValue,
      moduleStatus,
      onFilterChange,
      onRowClick,
    ],
  );

  if (!decodedName) {
    return (
      <ModernPageLayout breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">
            {t("reporting.integrationNotFound", "Integration not found")}
          </p>
        </div>
      </ModernPageLayout>
    );
  }

  if (
    moduleQuery.isPending ||
    moduleQuery.isError ||
    !moduleQuery.data?.isActive
  ) {
    return null;
  }

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      title={t("integrations.modules.names.reporting", "Reporting")}
      subtitle={t(
        "reporting.tenantOverviewDescription",
        "Overview of reporting data import status per client",
      )}
    >
      {ui.length > 0 ? (
        <div className="space-y-4">
          {ui.map((component, index) => (
            <div key={`${component.component}-${index}`} className="space-y-4">
              {renderComponent(component, context)}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">
            {t(
              "reporting.noContentConfigured",
              "No content configured for this integration.",
            )}
          </p>
        </div>
      )}
    </ModernPageLayout>
  );
}

export default TenantReportingPage;
